import { Injectable } from "@angular/core";
import { Grifo, MarcacaoGrifaTexto } from "../../models/Grifo";
import { lastValueFrom, Observable, Subject, tap } from "rxjs";

import { LoggingService } from "../logging.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AppConfig } from "src/app/app.config";
import { ErrorHandlerService } from "../errorHandler.service";
import { SignalrService } from "../signalr.service";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { StorageService } from "../storage.service";
import { UsuarioPreferenciasService } from "./usuario.preferencias.service";
import { DataControleRepositorio } from "src/app/repositorios/dataControle.repositorio";
import { EnumTipoDataControle } from "src/app/enums/EnumDataControle";

@Injectable()
export class UsuarioGrifosService {
  readonly databaseName = "userdata";
  readonly collectionName = "grifos";

  private modificadoGrifo: Grifo = null;
  private modificadoGrifo$ = new Subject<Grifo>();

  constructor(
    private signalrService: SignalrService,
    private loggingService: LoggingService,
    private httpClient: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private usuarioPreferenciasService: UsuarioPreferenciasService,
    private storageService: StorageService,
    private dataControleRepositorio: DataControleRepositorio
  ) {
    signalrService
      .getMensagem()
      .subscribe((msg) => this.onMessageReceived(msg));
  }

  public async carregarGrifosPorLei(leiId: string): Promise<Grifo[]> {
    const url = `${AppConfig.apiEndpoint}/MarcacacoesGrifaTexto/MarcacacoesGrifos`;
    let requestParams = new HttpParams();
    let grifos: Grifo[];

    const grifoslocal = await this.storageService.list<Grifo>(
      this.databaseName,
      this.collectionName,
      (g) => g.idLei === leiId
    );

    if (grifoslocal.length > 0) {
      const cache = await this.VerificaCacheGrifos();

      if (cache) {
        return grifoslocal;
      }
    }

    requestParams = requestParams.append("IdLei", leiId);
    requestParams = requestParams.append(
      "userId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );
    await lastValueFrom(
      this.httpClient.get(url, { params: requestParams }).pipe(
        tap({
          next: (result: any) => {
            if (result?.marcacacoesGrifaTextoItens) {
              this.fromNuvem(result.marcacacoesGrifaTextoItens).catch(
                (error) => {
                  this.errorHandlerService.handleError(error);
                }
              );
              this.dataControleRepositorio
                .salvar(
                  this.dataControleRepositorio.generateDataControleTipo(
                    EnumTipoDataControle.DataVerificacaoCacheGrifos
                  )
                )
                .then(() => {})
                .catch((error) => this.errorHandlerService.handleError(error));
              grifos = result.marcacacoesGrifaTextoItens;
            } else {
              grifos = new Array<Grifo>();
            }
          },
          error: (error) => {
            this.errorHandlerService.handleError(error);
            if (grifoslocal) {
              grifos = grifoslocal;
            } else {
              grifos = new Array<Grifo>();
            }
          },
        })
      )
    );
    return grifos;
  }

  public async fromNuvem(grifos: Grifo[]): Promise<void> {
    return await this.storageService
      .upsertMany(grifos, this.databaseName, this.collectionName, false)
      .then(() => {})
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async atualizar(grifo: Grifo, sync = true): Promise<Grifo> {
    const novo = !grifo.dataHoraModificacao;
    return await this.storageService
      .upsert(grifo, this.databaseName, this.collectionName)
      .then((resultado) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = resultado;
          this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Grifos);
        }

        this.modificadoGrifo = resultado;

        this.updateModificadoGrifo();
        if (novo) {
          this.loggingService.logEvent("Leitor - Marcar Texto", null, null);
        }

        return resultado;
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Grifo();
      });
  }

  public async atualizarVarios(grifos: Grifo[], sync = true): Promise<Grifo[]> {
    const novo = !grifos[0].dataHoraModificacao;

    return await this.storageService
      .upsertMany(grifos, this.databaseName, this.collectionName)
      .then((resultado) => {
        if (sync) {
          const mensagens = resultado.map((g) => {
            const msg = new OperacaoSignalR();
            msg.dados = g;
            return msg;
          });

          this.signalrService.enviarMensagens(mensagens, EnumTipoObjeto.Grifos);
        }

        // this._modificado.next(grifos.map(g => g.idItem));
        resultado.forEach((g) => {
          this.modificadoGrifo = g;

          this.updateModificadoGrifo();
        });
        if (novo) {
          this.loggingService.logEvent("Leitor - Marcar texto", null, null);
        }
        return resultado;
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Grifo>();
      });
  }

  public remover(grifo: Grifo): Promise<Grifo> {
    grifo.removido = true;
    return this.atualizar(grifo);
  }

  public removerVarios(grifos: Grifo[]): Promise<Grifo[]> {
    grifos.forEach((g) => {
      g.removido = true;
    });

    return this.atualizarVarios(grifos);
  }

  public async buscarLei(idLei: string): Promise<Grifo[]> {
    return await this.storageService
      .list<Grifo>(
        this.databaseName,
        this.collectionName,
        (m) => m.idLei === idLei
      )
      .then((grifos) => grifos)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Grifo>();
      });
  }

  public async buscarLinha(
    idLinha: string,
    versaoLinha: number
  ): Promise<Grifo[]> {
    return await this.storageService
      .list<Grifo>(
        this.databaseName,
        this.collectionName,
        (c) => c.idItem === idLinha && c.idImportacao === versaoLinha
      )
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Grifo>();
      });
  }

  updateModificadoGrifo() {
    this.modificadoGrifo$.next(this.modificadoGrifo);
  }

  getModificadoGrifo(): Observable<Grifo> {
    return this.modificadoGrifo$.asObservable();
  }

  private onMessageReceived(msg: OperacaoSignalR): void {
    if (msg && msg.tipoObjeto === EnumTipoObjeto.Grifos) {
      this.atualizar(msg.dados, false);
    }
  }

  private async VerificaCacheGrifos(): Promise<boolean> {
    const lastDocumentsListCheck = await this.dataControleRepositorio.buscar(
      EnumTipoDataControle.DataVerificacaoCacheGrifos
    );
    const tempoEmMinutos = AppConfig.cacheConteudo;

    if (lastDocumentsListCheck) {
      const cacheConteudo: number = tempoEmMinutos * 60 * 1000;
      const ultimaModificacao: Date =
        lastDocumentsListCheck.dataHoraModificacao;
      const DataAtual: Date = new Date();

      if (DataAtual.getTime() - ultimaModificacao.getTime() < cacheConteudo) {
        return true;
      }
    }
    return false;
  }
}
